import { Typography } from '@chronosphereio/chrono-ui';
import type { ReactNode } from 'react';

export type FeedbackSeverity = 'error';

export interface QueryEditorFeedbackProps {
  /**
   * When `"error"`, text renders in the error palette with `role="alert"` and
   * `aria-live="assertive"`. When omitted, renders as plain `text.legacySecondary`
   * with no implicit ARIA role (uses `text.legacySecondary`).
   */
  severity?: FeedbackSeverity;
  children: ReactNode;
}

/**
 * Compact feedback strip for validation errors and plain messages.
 * Renders outside Container as a sibling — it describes the editor as a whole,
 * not a specific panel inside the bordered frame.
 *
 * Returns `null` when `children` is nullish, producing no DOM and no layout
 * shift.
 */
export function QueryEditorFeedback({ severity, children }: QueryEditorFeedbackProps) {
  if (!children) return null;

  const isError = severity === 'error';

  return (
    <Typography
      variant="body2"
      role={isError ? 'alert' : undefined}
      aria-live={isError ? 'assertive' : undefined}
      sx={{ color: isError ? 'error.main' : 'text.legacySecondary', pt: 0.5 }}
    >
      {children}
    </Typography>
  );
}
